import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import SpeedIcon from '@mui/icons-material/Speed';
import MDBox from '../components/MDBox';
import MDTypography from '../components/MDTypography';
import { useTheme } from '@mui/material/styles';
import GaugeChart from 'react-gauge-chart';
import { usePeriod } from '../components/PeriodContext';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import { CircularProgress } from '@mui/material';
import { db, collection, query, getDocs, where, doc, getDoc, setDoc } from '../firebaseConfig';

const getWeek = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  return Math.ceil(((d - new Date(d.getFullYear(), 0, 1)) / 86400000 + 1) / 7);
};

const TeamProfitFactorGaugeOverall = ({ teamId, userIDs }) => {
  const { selectedPeriod, selectedYear, selectedMonth, showMissedTrades, selectedTag } = usePeriod();
  const [profitFactor, setProfitFactor] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasData, setHasData] = useState(false);
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];

  useEffect(() => {
    const fetchProfitFactor = async () => {
      setLoading(true);
      if (!teamId || !userIDs || userIDs.length === 0) {
        console.log('TeamProfitFactorGaugeOverall: Missing teamId or userIDs', { teamId, userIDs });
        setProfitFactor(0);
        setHasData(false);
        setLoading(false);
        return;
      }

      try {
        const sevenDaysAgo = new Date(new Date().setDate(new Date().getDate() - 7));
        const teamDataRef = doc(db, 'teamData', teamId);
        const teamDataDoc = await getDoc(teamDataRef);

        // Check for cached team-wide profitFactor
        if (teamDataDoc.exists()) {
          const teamData = teamDataDoc.data();
          if (teamData.lastUpdated?.toDate() >= sevenDaysAgo && teamData.profitFactor) {
            setProfitFactor(parseFloat(teamData.profitFactor));
            setHasData(true);
            console.log('TeamProfitFactorGaugeOverall: Using cached profit factor:', teamData.profitFactor);
            setLoading(false);
            return;
          }
        }

        let allTrades = [];
        for (const userId of userIDs) {
          const userDataRef = doc(db, 'teamData', teamId, 'users', userId);
          const userDataDoc = await getDoc(userDataRef);

          let userTrades = [];
          let hasCachedData = false;

          if (userDataDoc.exists()) {
            const userData = userDataDoc.data();
            if (userData.lastUpdated?.toDate() >= sevenDaysAgo && userData.trades) {
              userTrades = userData.trades;
              hasCachedData = true;
              console.log(`TeamProfitFactorGaugeOverall: Using cached trades for user ${userId}:`, userTrades.length);
            }
          }

          if (!hasCachedData) {
            const accountsQuery = query(collection(db, `Users/${userId}/tradeAccounts`));
            const accountsDocs = await getDocs(accountsQuery);
            const tradeAccountIds = accountsDocs.docs.map((doc) => doc.id).concat('default');

            for (const accountId of tradeAccountIds) {
              const tradesQuery = query(
                collection(db, `Users/${userId}/tradeAccounts/${accountId}/trades`),
                where('status', '==', showMissedTrades ? 'Missed' : 'Closed')
              );
              const tradesSnapshot = await getDocs(tradesQuery);
              const tradesData = tradesSnapshot.docs
                .map((doc) => ({
                  id: doc.id,
                  date: doc.data().date,
                  returns: parseFloat(doc.data().returns) || 0,
                  tags: Array.isArray(doc.data().tags) ? doc.data().tags : [],
                }))
                .filter((trade) => {
                  if (!trade.date || !/^\d{4}-\d{2}-\d{2}$/.test(trade.date)) {
                    console.warn(`Invalid date format for trade ${trade.id}:`, trade.date);
                    return false;
                  }
                  const tradeDate = new Date(trade.date);
                  if (isNaN(tradeDate.getTime())) {
                    console.warn(`Invalid date object for trade ${trade.id}:`, trade.date);
                    return false;
                  }
                  return true;
                });
              userTrades = userTrades.concat(tradesData);
            }

            await setDoc(userDataRef, {
              trades: userTrades,
              lastUpdated: new Date(),
              period: 'all-time',
            }, { merge: true });
            console.log(`TeamProfitFactorGaugeOverall: Stored trades for user ${userId} in teamData/${teamId}/users/${userId}:`, userTrades.length);
          }

          allTrades = allTrades.concat(userTrades);
        }

        // Filter trades
        const filteredTrades = filterTrades(allTrades);

        // Calculate profit factor
        let totalWins = 0;
        let totalLosses = 0;
        filteredTrades.forEach((trade) => {
          const returns = trade.returns;
          if (returns > 0) {
            totalWins += returns;
          } else if (returns < 0) {
            totalLosses += Math.abs(returns);
          }
        });

        let calculatedProfitFactor = 0;
        if (totalLosses === 0 && totalWins === 0) {
          calculatedProfitFactor = 0;
        } else if (totalLosses === 0) {
          calculatedProfitFactor = 5;
        } else {
          calculatedProfitFactor = Math.min(totalWins / totalLosses, 5);
        }

        // Cache team-wide profit factor
        await setDoc(teamDataRef, {
          profitFactor: calculatedProfitFactor,
          lastUpdated: new Date(),
          period: selectedPeriod || 'all-time',
        }, { merge: true });
        console.log(`TeamProfitFactorGaugeOverall: Stored team profit factor in teamData/${teamId}:`, calculatedProfitFactor);

        setProfitFactor(calculatedProfitFactor);
        setHasData(filteredTrades.length > 0);
      } catch (error) {
        console.error('TeamProfitFactorGaugeOverall: Error fetching trades:', error);
        setProfitFactor(0);
        setHasData(false);
      } finally {
        setLoading(false);
      }
    };

    fetchProfitFactor();
  }, [teamId, userIDs, showMissedTrades, selectedPeriod, selectedYear, selectedMonth, selectedTag]);

  const filterTrades = (trades) => {
    return trades.filter((trade) => {
      const tradeDate = trade.date ? new Date(trade.date) : null;
      if (!tradeDate || isNaN(tradeDate.getTime())) return false;
      const matchesPeriod = () => {
        if (selectedPeriod === 'today') {
          return tradeDate.toDateString() === new Date().toDateString();
        } else if (selectedPeriod === 'this-week') {
          return getWeek(tradeDate) === getWeek(new Date()) && tradeDate.getFullYear() === new Date().getFullYear();
        } else if (selectedPeriod === 'this-month') {
          return tradeDate.getMonth() === new Date().getMonth() && tradeDate.getFullYear() === new Date().getFullYear();
        } else if (selectedPeriod === 'this-year') {
          return tradeDate.getFullYear() === new Date().getFullYear();
        } else if (selectedPeriod === 'pick-month') {
          return tradeDate.getMonth() + 1 === selectedMonth && tradeDate.getFullYear() === selectedYear;
        } else if (selectedPeriod === 'pick-year') {
          return tradeDate.getFullYear() === selectedYear;
        } else if (selectedPeriod === 'pick-month-year') {
          return tradeDate.getMonth() + 1 === selectedMonth && tradeDate.getFullYear() === selectedYear;
        }
        return true; // all-time
      };
      const matchesTag = selectedTag === 'all' || (Array.isArray(trade.tags) && trade.tags.includes(selectedTag));
      return matchesPeriod() && matchesTag;
    });
  };

  const upperLimit = 5;
  const gaugeValue = Math.min(profitFactor / upperLimit, 1);
  const profitFactorColor = profitFactor >= 1 ? 'success' : 'error';

  const tooltipContent = `Team Profit Factor measures trading effectiveness for ${
    showMissedTrades ? 'missed' : 'closed'
  } trades across all team members.\n\nCalculated as total profits divided by total losses.\n\nBelow 1: Losing strategy.\n1-2: Decent performance.\n2-3: Good performance.\n3+: Excellent performance.\n\nCapped at 5 for display.`;

  const renderContent = () => {
    if (loading) {
      return (
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </MDBox>
      );
    }

    if (!hasData) {
      return (
        <MDBox
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
          textAlign="center"
          px={2}
        >
          <InfoIcon sx={{ fontSize: 40, mb: 2, color: iconColor }} />
          <MDTypography variant="h6" color="text">
            No trading data available yet
          </MDTypography>
          <MDTypography variant="body2" color="text">
            Team members need to complete trades to see performance charts
          </MDTypography>
        </MDBox>
      );
    }

    return (
      <MDBox pb={2} px={2} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
        <GaugeChart
          id="profit-factor-gauge-overall"
          nrOfLevels={20}
          colors={['#FF0000', '#FFFF00', '#00FF00']}
          arcWidth={0.3}
          percent={gaugeValue}
          hideText={true}
          needleColor="#345243"
          formatTextValue={() => ''}
          style={{ height: '110px', width: '100%', maxWidth: '250px' }}
          animate={false}
        />
        <MDBox display="flex" justifyContent="space-between" width="100%" flexWrap="wrap">
          <MDTypography component="span" variant="button" fontWeight="bold" fontFamily="inherit" color="error">
            Poor
          </MDTypography>
          <MDTypography component="span" variant="button" fontWeight="bold" fontFamily="inherit" color="success">
            Excellent
          </MDTypography>
        </MDBox>
        <MDTypography
          component="span"
          variant="button"
          fontWeight="bold"
          fontFamily="inherit"
          color={profitFactorColor}
        >
          {profitFactor.toFixed(2)}
        </MDTypography>
      </MDBox>
    );
  };

  return (
    <Card sx={{ minHeight: '300px', padding: '16px' }}>
      <MDBox display="flex" flexDirection="column" justifyContent="space-between" pt={1} px={2} height="100%">
        <MDBox display="flex" justifyContent="space-between" alignItems="center">
          <MDBox
            variant="gradient"
            bgColor="light"
            color="dark"
            coloredShadow="light"
            borderRadius="xl"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="4rem"
            height="4rem"
            mt={-5}
          >
            <SpeedIcon fontSize="medium" color="inherit" />
          </MDBox>
          <MDBox textAlign="right" lineHeight={1.25}>
            <MDTypography variant="button" fontWeight="light" color="text" fontFamily="inherit">
              Team Profit Factor {showMissedTrades ? '(Missed)' : ''}
            </MDTypography>
            <Tooltip
              title={tooltipContent}
              placement="bottom-end"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    border: `1px solid ${theme.palette.divider}`,
                    p: 1.5,
                    maxWidth: '250px',
                    fontSize: '0.75rem',
                    lineHeight: 1.5,
                    '& .MuiTooltip-arrow': {
                      color: theme.palette.background.paper,
                    },
                  },
                },
              }}
            >
              <InfoIcon
                sx={{
                  ml: 1,
                  fontSize: '1rem',
                  color: iconColor,
                  cursor: 'help',
                }}
              />
            </Tooltip>
            <MDTypography variant="h4" fontFamily="inherit" style={{ visibility: 'hidden' }}>
              0
            </MDTypography>
          </MDBox>
        </MDBox>
        <Divider />
        {renderContent()}
      </MDBox>
    </Card>
  );
};

TeamProfitFactorGaugeOverall.propTypes = {
  teamId: PropTypes.string.isRequired,
  userIDs: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default TeamProfitFactorGaugeOverall;